import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface TestimonialData {
  name: string;
  role: string;
  content: string;
  rating: number;
  image: string;
}

export interface AdminSettings {
  id?: string;
  
  // Site Configuration
  site_title: string;
  site_description: string;
  site_favicon_url: string;
  site_logo_url: string;
  
  // Hero Section
  hero_title: string;
  hero_subtitle: string;
  hero_description: string;
  hero_background_url: string;
  hero_video_url?: string;
  hero_cta_text: string;
  hero_secondary_cta_text: string;
  
  // Contact Information
  contact_phone: string;
  contact_email: string;
  contact_address: string;
  whatsapp_number: string;
  
  // Social Media
  social_facebook?: string;
  social_twitter?: string;
  social_linkedin?: string;
  social_instagram?: string;
  social_youtube?: string;
  
  // Business Info
  company_name: string;
  business_hours: string;
  timezone: string;
  
  // Profile Settings (Legacy - keeping for backward compatibility)
  sara_name: string;
  sara_role: string;
  sara_image_url: string;
  show_video_instead_of_sara: boolean;
  
  // Chat Settings
  initial_chat_message: string;
  online_status_enabled: boolean;
  zoom_meeting_url: string;
  chat_agent_name: string;
  chat_agent_image_url: string;
  
  // Reviews/Testimonials
  show_reviews: boolean;
  reviews_title: string;
  reviews_subtitle: string;
  testimonials: TestimonialData[];
  
  // Form Configuration
  phone_placeholder: string;
  form_title: string;
  form_subtitle: string;
  collect_name: boolean;
  collect_email: boolean;
  collect_phone: boolean;
  collect_company: boolean;
  custom_form_fields: any[];
  
  // Telegram Integration
  telegram_bot_token?: string;
  telegram_chat_id?: string;
  telegram_webhook_url?: string;
  telegram_enabled: boolean;
  
  // Tracking & Analytics
  google_analytics_id?: string;
  google_tag_manager_id?: string;
  facebook_pixel_id?: string;
  tiktok_pixel_id?: string;
  custom_head_code?: string;
  custom_body_code?: string;
  custom_footer_code?: string;
  
  // CTA & Conversion
  primary_cta_text: string;
  secondary_cta_text: string;
  urgency_text: string;
  trust_badges_enabled: boolean;
  countdown_enabled: boolean;
  countdown_minutes: number;
  
  // Service/Product Info
  service_name: string;
  service_description: string;
  pricing_text: string;
  guarantee_text: string;
  
  // Lead Generation
  lead_magnet_enabled: boolean;
  lead_magnet_title: string;
  lead_magnet_description: string;
  lead_magnet_file_url?: string;
  
  // Email Integration
  email_provider: string;
  email_api_key?: string;
  notification_email: string;
  autoresponder_enabled: boolean;
  autoresponder_subject: string;
  autoresponder_message: string;
  
  // Advanced Customization
  custom_css?: string;
  custom_javascript?: string;
  maintenance_mode: boolean;
  maintenance_message: string;
  
  // SEO
  meta_keywords: string;
  meta_author: string;
  canonical_url?: string;
  og_image_url?: string;
  twitter_card_type: string;
  
  // Conversion Optimization
  ab_testing_enabled: boolean;
  ab_test_variants: any[];
  exit_intent_enabled: boolean;
  exit_intent_message: string;
  
  // Footer & Legal
  footer_text: string;
  footer_links: any[];
  privacy_policy_url: string;
  terms_of_service_url: string;
  cookie_consent_enabled: boolean;
  gdpr_compliance_enabled: boolean;
}

const defaultSettings: AdminSettings = {
  // Site Configuration
  site_title: 'Vintage Fiasco Wholesale',
  site_description: 'Cam‑picking wholesale via live video — book, pick, and order easily.',
  site_favicon_url: '/favicon.ico',
  site_logo_url: '/lovable-uploads/360cb7d8-3552-4109-96ba-7bc8f73917b4.png',
  
  // Hero Section
  hero_title: 'Buy Wholesale Vintage Clothes on Camera',
  hero_subtitle: 'Get Expert Help in 10 Minutes',
  hero_description: 'Our security experts are standing by to fix your hacked, broken, or compromised website immediately.',
  hero_background_url: '/lovable-uploads/439e592b-a14b-43c7-b4be-b85beffc5f5c.png',
  hero_cta_text: 'Get Immediate Help',
  hero_secondary_cta_text: 'Schedule Callback',
  
  // Contact Information
  contact_phone: '+49 15754767196',
  contact_email: 'hello@vintagefiasco.com',
  contact_address: 'Mühlenstraße 53, 50354 Hürth, Germany',
  whatsapp_number: '+49 15754767196',
  
  // Business Info
  company_name: 'Vintage Fiasco',
  business_hours: 'Mo–Sa 9:30 – 17:30',
  timezone: 'UTC',
  
  // Legacy Profile Settings
  sara_name: 'Rebecca Molitor',
  sara_role: 'Owner • Vintage Fiasco Wholesale',
  sara_image_url: '/images/sara-headshot.png',
  show_video_instead_of_sara: false,
  
  // Chat Settings
  initial_chat_message: "Hi! 👋 I'm here to help with your website emergency. Need immediate assistance? Feel free to ask questions, **share screen** via our zoom meeting, or just chat with me!",
  online_status_enabled: true,
  zoom_meeting_url: 'https://us04web.zoom.us/j/4293507554?pwd=MyHMpmALzmbqRiVtl8YpMpP9ztV9Ea.1',
  chat_agent_name: 'Sarah (Support)',
  chat_agent_image_url: '/images/sara-headshot.png',
  
  // Reviews/Testimonials
  show_reviews: true,
  reviews_title: 'What Our Clients Say',
  reviews_subtitle: 'Trusted by 500+ businesses worldwide',
  testimonials: [
    {
      name: "Elena K.",
      role: "Owner, Retro Finds",
      content: "Great quality and fast service. Loved picking items live on camera — super convenient!",
      rating: 5,
      image: "https://lh3.googleusercontent.com/a-/ALV-UjWfWTRzchQW3tyoeNVTqYTr_kZJ3dLR9nA8a1sJ4mMZaIOCW_E=w108-h108-p-rp-mo-br100"
    },
    {
      name: "Martin S.",
      role: "Boutique Buyer",
      content: "Exactly what I needed for my store. The video call selection saved me so much time.",
      rating: 5,
      image: "https://lh3.googleusercontent.com/a-/ALV-UjUk7brRSyGga-W_kzeN8gHeehzyoGt9yONu3d5DmRpvcv_kY2dL1w=w108-h108-p-rp-mo-br100"
    },
    {
      name: "Ayesha R.",
      role: "Vintage Curator",
      content: "Authentic pieces and excellent support. Highly recommended for wholesale vintage.",
      rating: 5,
      image: "https://lh3.googleusercontent.com/a-/ALV-UjX6-Cu4CeuauTYIJkwdbqZgcRpyhANSMcfSml4kkUz-q8LthZn5=w108-h108-p-rp-mo-br100"
    }
  ],
  
  // Form Configuration
  phone_placeholder: 'Your Phone Number',
  form_title: 'Get Immediate Help',
  form_subtitle: 'Tell us about your emergency',
  collect_name: true,
  collect_email: true,
  collect_phone: true,
  collect_company: false,
  custom_form_fields: [],
  
  // Telegram Integration
  telegram_enabled: false,
  
  // CTA & Conversion
  primary_cta_text: 'Get Help Now',
  secondary_cta_text: 'Learn More',
  urgency_text: 'Available 24/7 for emergency response',
  trust_badges_enabled: true,
  countdown_enabled: false,
  countdown_minutes: 15,
  
  // Service/Product Info
  service_name: 'Website Emergency Recovery',
  service_description: 'Professional website recovery and security services',
  pricing_text: 'Starting at $199',
  guarantee_text: '100% Satisfaction Guaranteed',
  
  // Lead Generation
  lead_magnet_enabled: false,
  lead_magnet_title: 'Free Website Security Checklist',
  lead_magnet_description: 'Download our comprehensive security checklist',
  
  // Email Integration
  email_provider: 'none',
  notification_email: 'hello@vintagefiasco.com',
  autoresponder_enabled: true,
  autoresponder_subject: 'We received your request',
  autoresponder_message: 'Thank you for contacting us. We will get back to you within 10 minutes.',
  
  // Advanced Customization
  maintenance_mode: false,
  maintenance_message: 'Site temporarily unavailable for maintenance',
  
  // SEO
  meta_keywords: 'website emergency, website recovery, website repair, security',
  meta_author: 'Dash22',
  twitter_card_type: 'summary_large_image',
  
  // Conversion Optimization
  ab_testing_enabled: false,
  ab_test_variants: [],
  exit_intent_enabled: true,
  exit_intent_message: 'Wait! Get 20% off your first service',
  
  // Footer & Legal
  footer_text: '© 2025 Vintage Fiasco. All rights reserved.',
  footer_links: [
    { text: 'Privacy Policy', url: '/privacy' },
    { text: 'Terms of Service', url: '/terms' }
  ],
  privacy_policy_url: '/privacy',
  terms_of_service_url: '/terms',
  cookie_consent_enabled: true,
  gdpr_compliance_enabled: false
};

export const useAdminSettings = () => {
  // Add SEO fields to defaultSettings
  const settingsWithSEO = {
    ...defaultSettings,
    // Add missing SEO fields
    seo_meta_title: 'Vintage Fiasco Wholesale – Cam‑Picking via Live Video',
    seo_meta_description: 'Book a video appointment, pick categories on camera, and place your wholesale order quickly. Minimum 500€ net from 4+ categories.',
    seo_meta_keywords: 'website recovery, emergency site fix, hacked site help, WordPress recovery, site down support, fast website repair, malware removal, website security, emergency website support, website restoration, cyber security fix',
    seo_canonical_url_base: 'https://vintagefiasco.com/',
    
    // Additional fields that might be used
    hero_callback_time: '10 minutes',
    video_youtube_url: 'https://www.youtube.com/embed/wPaQDfiXN7M?si=a_lbL0wPXkQ0TYWq&autoplay=1&mute=1&controls=0&modestbranding=1&rel=0&showinfo=0',
    // UI header support
    phone_number: '+49 15754767196'
  };

  const [settings] = useState<AdminSettings & any>(settingsWithSEO);
  const [loading] = useState(false);

  const updateSettings = async (newSettings: Partial<AdminSettings>) => {
    // Hard-coded settings update simulation
    console.log('Settings update requested:', newSettings);
    return { error: null };
  };

  return {
    settings,
    loading,
    updateSettings
  };
};